'use client';

import {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { Carousel, CarouselApi, SwiperSlide } from '@/components/ui/carousel';
import { LinkLocationProvider } from '@/components/ui/link-location-context';
import { Skeleton } from '@/components/ui/skeleton';
import { ToolUIErrorBoundary } from '@/components/ui/tool-ui-error-boundary';
import { fadeStyle, FADE_DURATION_MS } from '@/hooks/use-fade-transition';
import { safeSessionStorage } from '@/lib/storage/safe-storage';
import { cn } from '@/lib/utils';
import { createDebouncedResizeObserver } from '@/lib/utils/resize-observer';

export { FADE_DURATION_MS as CONTENT_FADE_MS } from '@/hooks/use-fade-transition';

import type { Swiper as SwiperClass, SwiperOptions } from 'swiper/types';

const EMPTY_ITEMS: never[] = [];
export const DRAG_DEBOUNCE_MS = 150;

function slideStorageKey(itemsKey: string): string {
  return `carousel-slide-${itemsKey}`;
}
function itemStorageKey(itemsKey: string): string {
  return `carousel-item-${itemsKey}`;
}

const CAROUSEL_OPTS: SwiperOptions = {
  slidesPerView: 'auto',
  centeredSlides: true,
  centeredSlidesBounds: true,
  spaceBetween: 16,
  grabCursor: true,
  slideToClickedSlide: true,
  observer: true,
  observeParents: true,
};

export interface ItemCarouselRef {
  slideTo: (index: number, speed?: number) => void;
}

interface ItemCarouselProps<T> {
  items?: T[] | null;
  renderItem: (item: T, index: number) => ReactNode;
  header?: ReactNode;
  /** Rendered below the carousel slides (and below the empty state). Not shown during the loading state. */
  footer?: ReactNode;
  emptyState?: ReactNode;
  loadingState?: ReactNode;
  isLoading?: boolean;
  withLoadingState?: boolean;
  showDots?: boolean;
  peekEnabled?: boolean;
  maxItemWidth?: string;
  onReachEnd?: () => void;
  onLoadMore?: () => void | Promise<void>;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  className?: string;
  /** Extra class applied to the carousel content wrapper (below the header). */
  contentClassName?: string;
  /** Change to trigger a fade-out → fade-in transition on the content without re-mounting it. */
  contentKey?: number | string;
  /** Holds the content faded out while a caller runs its own transition (see `useFadeTransition`).
   * A caller can't do this through `contentClassName`, since the inline opacity below wins. */
  contentFaded?: boolean;
  itemsKey?: string;
  /** Change to jump to the first slide after a deliberate re-sort, instead of
   * tracking the previously-active card. */
  resetToken?: number | string;
  onActiveIndexChange?: (index: number) => void;
  /** Extract a stable key from an item. Used for slide key and to preserve the active slide when items change (e.g. after merge). Return undefined for items without a stable identity. */
  getItemKey?: (item: T) => string | undefined;
}

function resolveStartIndex<T>({
  items,
  getItemKey,
  itemsKey,
}: {
  items: T[];
  getItemKey?: (item: T) => string | undefined;
  itemsKey?: string;
}): number {
  if (items.length === 0 || !itemsKey) return 0;

  const savedItemKey = safeSessionStorage.getItem(itemStorageKey(itemsKey));

  // Prefer restoring by item key (stable across reordering / merge).
  if (savedItemKey && getItemKey) {
    const idx = items.findIndex(item => {
      const key = getItemKey(item);
      return key !== undefined && key === savedItemKey;
    });
    if (idx >= 0) return idx;
  }

  // Fall back to saved numeric index.
  const savedIndex = parseInt(safeSessionStorage.getItem(slideStorageKey(itemsKey)) || '0', 10);
  return Math.min(savedIndex, items.length - 1);
}

// getItemKey values aren't guaranteed unique — e.g. some suppliers hand out
// one generic booking link for several results, so keying by it alone would
// collide. Disambiguate deterministically: the first occurrence keeps its
// stable key (so reconciliation of the unique common case is unaffected),
// later duplicates get a positional suffix. A suffixed key can itself collide
// with a real key another item carries (['x', 'x', 'x-1']), so keep suffixing
// until the key is one no other slide claimed.
export function buildSlideKeys<T>(
  items: readonly T[],
  getItemKey?: (item: T) => string | undefined,
): string[] {
  const seen = new Set<string>();
  return items.map((item, index) => {
    let key = getItemKey?.(item) ?? String(index);
    while (seen.has(key)) key = `${key}-${index}`;
    seen.add(key);
    return key;
  });
}

function ItemCarouselInner<T>(
  {
    items: itemsProp,
    renderItem,
    header,
    footer,
    emptyState,
    loadingState,
    isLoading = false,
    withLoadingState = false,
    showDots = true,
    peekEnabled = true,
    maxItemWidth = '36rem',
    onReachEnd,
    onLoadMore,
    isLoadingMore = false,
    hasMore = false,
    className,
    contentClassName,
    contentKey,
    contentFaded = false,
    itemsKey,
    resetToken,
    onActiveIndexChange,
    getItemKey,
  }: ItemCarouselProps<T>,
  ref: React.ForwardedRef<ItemCarouselRef>,
) {
  const items = itemsProp ?? EMPTY_ITEMS;
  const [api, setApi] = useState<CarouselApi>(null);
  const activeIndexRef = useRef(0);
  const activeItemKeyRef = useRef<string | undefined>(undefined);
  const activeIndexTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Suppresses item-key preservation when contentKey changes (e.g. board type
  // filter). Set to the items array at the time of the contentKey change so we
  // can detect when items actually re-sort in a later render.
  const contentResetItemsRef = useRef<T[] | null>(null);
  const prevItemsRef = useRef(items);
  const [contentFade, setContentFade] = useState<{
    key: number | string | undefined;
    faded: boolean;
  }>({ key: contentKey, faded: false });

  if (contentFade.key !== contentKey) {
    setContentFade({ key: contentKey, faded: true });
  }

  const isFirstApiInit = useRef(true);

  useImperativeHandle(
    ref,
    () => ({
      slideTo: (index, speed) => api?.slideTo(index, speed),
    }),
    [api],
  );

  const updateActiveIndex = useCallback(
    (newIndex: number, { isDragging = false }: { isDragging?: boolean } = {}) => {
      if (newIndex === activeIndexRef.current) return;
      activeIndexRef.current = newIndex;

      if (!contentResetItemsRef.current) {
        const key = getItemKey?.(items[newIndex]);
        if (key) activeItemKeyRef.current = key;
      }

      if (itemsKey) {
        safeSessionStorage.setItem(slideStorageKey(itemsKey), String(newIndex));
        if (activeItemKeyRef.current) {
          safeSessionStorage.setItem(itemStorageKey(itemsKey), activeItemKeyRef.current);
        }
      }

      clearTimeout(activeIndexTimerRef.current);

      if (isDragging) {
        // Drag: debounce to prevent chip flicker at group boundaries
        activeIndexTimerRef.current = setTimeout(() => {
          onActiveIndexChange?.(activeIndexRef.current);
        }, DRAG_DEBOUNCE_MS);
      } else {
        // Swipe/snap/programmatic: fire immediately to stay in sync with dots
        onActiveIndexChange?.(newIndex);
      }

      const isAtLastSlide = newIndex === items.length - 1;
      if (isAtLastSlide) {
        onReachEnd?.();
        if (hasMore && !isLoadingMore) {
          onLoadMore?.();
        }
      }
    },
    [
      items,
      getItemKey,
      onReachEnd,
      onLoadMore,
      hasMore,
      isLoadingMore,
      itemsKey,
      onActiveIndexChange,
    ],
  );

  const handleSlideChange = useCallback(
    (swiper: SwiperClass, isDragging: boolean) =>
      updateActiveIndex(swiper.activeIndex, { isDragging }),
    [updateActiveIndex],
  );

  const handleSwiperInit = useCallback(
    (swiper: CarouselApi) => {
      setApi(swiper);
    },
    [setApi],
  );

  const prevContentKeyRef = useRef(contentKey);
  const prevResetTokenRef = useRef(resetToken);

  useEffect(() => {
    if (!api) return;

    // Deliberate re-sort: jump to the top and skip item-key restoration. Also
    // rewrite the persisted position so a later remount restores the top, not the
    // pre-reset slide.
    if (prevResetTokenRef.current !== resetToken) {
      prevResetTokenRef.current = resetToken;
      activeItemKeyRef.current = undefined;
      activeIndexRef.current = 0;
      api.slideTo(0, 0);
      if (itemsKey) {
        safeSessionStorage.setItem(slideStorageKey(itemsKey), '0');
        safeSessionStorage.removeItem(itemStorageKey(itemsKey));
      }
      onActiveIndexChange?.(0);
      api.update();
      return;
    }

    // Content key changed (e.g. board type filter) — the caller already
    // called slideTo(0), so skip key-based index restoration for this update.
    // Snapshot the current items so we can detect when they actually re-sort.
    // While the snapshot is set, updateActiveIndex also skips saving item keys
    // (prevents the programmatic slideTo(0) from re-setting the key).
    const contentKeyChanged = prevContentKeyRef.current !== contentKey;
    const itemsChanged = prevItemsRef.current !== items;
    prevItemsRef.current = items;

    if (contentKeyChanged) {
      prevContentKeyRef.current = contentKey;
      activeItemKeyRef.current = undefined;
      // Only snap to 0 when the caller changed contentKey in isolation (T4
      // board-type flow: contentKey flips first, items re-sort after a timeout).
      // When items also change in the same render — e.g. streaming tool groups
      // that derive contentKey from items.length — forcing slide 0 on every new
      // item would yank the user back to the start mid-stream.
      if (!itemsChanged) {
        activeIndexRef.current = 0;
        api.slideTo(0, 0);
      }
      contentResetItemsRef.current = itemsChanged ? null : items;
    } else if (contentResetItemsRef.current && itemsChanged) {
      // Items re-sorted after the T4 contentKey reset — clear the snapshot
      // and the item key so key-based restoration doesn't bring the pre-reset
      // item back. Position is already at 0 from the contentKey-branch slide.
      contentResetItemsRef.current = null;
      activeItemKeyRef.current = undefined;
    }

    if (isFirstApiInit.current) {
      isFirstApiInit.current = false;

      // On mount: restore position by item key (preferred) or saved index.
      const resolvedIndex = resolveStartIndex({ items, getItemKey, itemsKey });
      if (resolvedIndex > 0) {
        api.slideTo(resolvedIndex, 0);
      }
      activeIndexRef.current = resolvedIndex;
      activeItemKeyRef.current = getItemKey?.(items[resolvedIndex]) ?? undefined;
      return;
    }

    // Items changed mid-lifecycle (e.g. merge added items).
    // Try to stay on the same item by matching its key in the new array.
    if (items.length > 0 && getItemKey && activeItemKeyRef.current) {
      const targetIndex = items.findIndex(item => getItemKey(item) === activeItemKeyRef.current);
      if (targetIndex >= 0 && targetIndex !== api.activeIndex) {
        api.slideTo(targetIndex, 0);
        activeIndexRef.current = targetIndex;
      }
    } else if (items.length > 0) {
      const currentIndex = api.activeIndex;
      if (currentIndex >= items.length) {
        api.slideTo(items.length - 1, 0);
      }
    }

    api.update();
  }, [api, items, itemsKey, getItemKey, contentKey, resetToken, onActiveIndexChange]);

  // Recalculate Swiper layout when the container width changes.
  // Handles HMR/fast-reload AND Storybook story-to-story navigation where
  // the iframe width settles after Swiper has already initialized.
  useEffect(() => {
    if (!api?.el) return;
    const el = api.el;
    const { observer, disconnect } = createDebouncedResizeObserver(() => {
      api.update();
    });
    observer.observe(el);
    return () => disconnect();
  }, [api]);

  useEffect(() => () => clearTimeout(activeIndexTimerRef.current), []);

  useEffect(() => {
    if (!contentFade.faded) return;
    const timer = setTimeout(
      () => setContentFade(prev => ({ ...prev, faded: false })),
      FADE_DURATION_MS,
    );
    return () => clearTimeout(timer);
  }, [contentFade.faded]);

  if (isLoading && items.length === 0) {
    if (!withLoadingState) return null;
    return (
      <div className={className}>
        {loadingState || <Skeleton className="h-64 w-full rounded-2xl" />}
      </div>
    );
  }

  const isEmpty = !isLoading && items.length === 0;

  if (isEmpty) {
    // Nothing to say about an empty result: render no wrapper at all, so a suppressed empty state
    // leaves no vertical gap where the carousel would have been.
    if (!emptyState && !header && !footer) return null;
    return (
      <div className="my-3 overflow-x-clip">
        {header}
        {/* Same fade treatment as the carousel content, so switching from a
            populated carousel to the empty state fades in instead of snapping. */}
        <div
          className={cn(contentClassName, 'transition-opacity ease-in-out')}
          // Duration from the constant the timer below uses, so the two cannot drift apart.
          style={fadeStyle(contentFade.faded || contentFaded)}
        >
          {emptyState}
        </div>
        {footer}
      </div>
    );
  }

  const slideKeys = buildSlideKeys(items, getItemKey);

  return (
    <LinkLocationProvider location="carousel">
      <div className="carousel-full-bleed my-3">
        {header}
        <div
          className={cn(contentClassName, 'transition-opacity ease-in-out')}
          style={fadeStyle(contentFade.faded || contentFaded)}
        >
          <Carousel
            setApi={handleSwiperInit}
            className="relative w-full"
            opts={CAROUSEL_OPTS}
            onSlideChange={handleSlideChange}
            showPagination={showDots && items.length > 1}
            showNavigation={items.length > 1}
            tabIndex={0}
          >
            {items.map((item, index) => (
              <SwiperSlide
                key={slideKeys[index]}
                className={cn('bzk-carousel-slide', peekEnabled && 'carousel-slide-peek')}
                style={{ maxWidth: maxItemWidth }}
              >
                <div className="relative h-full">
                  <ToolUIErrorBoundary>{renderItem(item, index)}</ToolUIErrorBoundary>
                  <div className="carousel-slide-overlay absolute inset-0 z-10 cursor-pointer" />
                </div>
              </SwiperSlide>
            ))}
          </Carousel>
        </div>
        {footer}
      </div>
    </LinkLocationProvider>
  );
}

export const ItemCarousel = forwardRef(ItemCarouselInner) as <T>(
  props: ItemCarouselProps<T> & { ref?: React.ForwardedRef<ItemCarouselRef> },
) => ReactNode;
